<template>
    <div 
        class="table-container"
        :class="{
            'px-3' : $breakpoint.xsOnly,
            'border-top' : $breakpoint.smAndDown
        }"
    >
        <div 
            class="row mx-0"
            :class="[$breakpoint.smAndDown ? 'pt-3 pb-2' : 'py-3']"
        >
            <div class="col-xs-12 col-sm-6 col-md-6 d-flex justify-content-start">
                <h5 class="text-left">
                    <i class="fas fa-inbox mr-2"></i>Received Negotiations: {{fuelGroupSelected.fuel._id | fueltype}}
                </h5>
            </div>
        </div>
        <div v-if="negotiations.length < 1"
            class="alert alert-secondary mt-3 text-center">
            <i class="fas fa-info-circle mr-2"></i>
            No negotiations placed
        </div>
        <table v-else class="table responsive-table">
            <thead v-if="negotiations.length > 0">
                <tr>
                    <th v-for="(header, index) in tableHeaders[fuelGroupSelected.fuel.type]" :key="index">
                        {{header}}
                        <span v-if="index !== tableHeaders[fuelGroupSelected.fuel.type].length - 1">
                            <span v-if="header.toLowerCase() === 'buyer' || 
                                        header.toLowerCase() === 'terminal' || 
                                        header.toLowerCase() === 'measurement' || 
                                        header.toLowerCase() === ''">
                                <i v-if="activeSortColumn == header.toLowerCase() && activeSortDirection == 'asc'" 
                                    class="fas fa-sort-alpha-down sort-icon" :class="{'selected': activeSortColumn == header.toLowerCase() && activeSortDirection == 'asc'}" 
                                    size="lg" @click="sortByColumn(header, 'desc')"></i>
                                <i v-else class="fas sort-icon" 
                                    :class="activeSortColumn == header.toLowerCase() && activeSortDirection == 'desc'? ['selected','fa-sort-alpha-up'] : 'fa-sort-alpha-down' " 
                                    size="lg" @click="sortByColumn(header, 'asc')"></i>
                            </span>
                            <span v-else>
                                <i v-if="activeSortColumn == header.toLowerCase() && activeSortDirection == 'asc'" 
                                    class="fas fa-sort-numeric-down sort-icon" :class="{'selected': activeSortColumn == header.toLowerCase() && activeSortDirection == 'asc'}" 
                                    size="lg" @click="sortByColumn(header, 'desc')"></i>
                                <i v-else class="fas sort-icon" 
                                    :class="activeSortColumn == header.toLowerCase() && activeSortDirection == 'desc'? ['selected','fa-sort-numeric-up'] : 'fa-sort-numeric-down' " 
                                    size="lg" @click="sortByColumn(header, 'asc')"></i>  
                            </span>
                        </span>
                    </th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="(negotiation, key) in negotiations" :key="key" 
                    @click="selectTab(key, negotiation._id)" 
                    :class="{'select-row': wasMyCounterAccepted[key] ||
                        (getNegotiationListActiveStatus[key] && negotiation.status_id == 'ACTIVE') ||
                        (negotiation.status_id == 'COMPLETED' && negotiation.accepting_user.account.id == userLoggedIn.id),
                        'mx-3 w-100' : $breakpoint.mdAndDown && !$breakpoint.xsOnly, 'py-2' : $breakpoint.mdAndDown}">
                    <td label="Buyer">
                        <span v-if="negotiation.buyer">{{negotiation.buyer.firstName + ' ' + negotiation.buyer.lastName}}</span>
                        <span v-else>N/A</span>
                    </td>
                    <td label="Time Remaining" class="countdown-cell" :class="{'reduced-padding': getNegotiationListActiveStatus[key]}">
                        <span v-if="getNegotiationListActiveStatus[key] && negotiation.status_id == 'ACTIVE'">
                            <!-- Progress circle to represent time remaining -->
                            <span class="countdown">
                                <radial-progress-bar class="countdown-progress" :diameter="35" 
                                    :totalSteps="(new Date(negotiation.expires) - new Date(negotiation.created))/1000" 
                                    :completedSteps="getNegotiationListTimer[key]" />
                            </span>

                            <!-- Display time remaining as a tooltip -->
                            <span class="countdown-tooltip">
                                {{getNegotiationListTimer[key] | countdownshortformat}}
                            </span>
                        </span>
                        <span v-else class="text-danger">
                            <i class="fas fa-times"></i>
                        </span>
                    </td>
                    <td label="Terminal" v-if="fuelGroupSelected.fuel.type == 'SPOT'">
                        <span v-if="wasMyCounterAccepted[key]">
                            {{negotiation.counterOffers.find(counter => counter._id == negotiation.accepted_counter_offer_id)
                                .terminal.name}}
                        </span>
                        <span v-else>
                            <span v-if="negotiation.terminal">{{negotiation.terminal.name}}</span>
                            <span v-else>N/A</span>
                        </span>
                    </td>
                    <td label="Vol/L">
                        <span>
                        {{wasMyCounterAccepted[key] ?
                            negotiation.counterOffers.find(counter => counter._id == negotiation.accepted_counter_offer_id).quantity :
                            negotiation.quantity | volumeformat }}
                        </span>
                    </td>
                    <td label="Measurement">
                        <span>
                        {{wasMyCounterAccepted[key] ?
                            negotiation.counterOffers.find(counter => counter._id == negotiation.accepted_counter_offer_id).fuel_measurement_id :
                            negotiation.fuel_measurement_id | fuelmeasurementtype }}
                        </span>
                    </td>
                    <td label="Lifting Period" v-if="fuelGroupSelected.fuel.type !== 'BUNKERING'">
                        <span>
                        {{wasMyCounterAccepted[key] ?
                            negotiation.counterOffers.find(counter => counter._id == negotiation.accepted_counter_offer_id).collectionTime :
                            negotiation.collectionTime | collectiondaysformat }}
                        </span>
                    </td>
                    <td label="Payment Terms">
                        <span v-if="negotiation.buyer">{{negotiation.buyer.credit.days | creditdaysformat}}</span>
                        <span v-else>N/A</span>
                    </td>
                    <td label="Price">
                        <span v-if="wasMyCounterAccepted[key]">
                            {{negotiation.counterOffers.find(counter => counter._id == negotiation.accepted_counter_offer_id).price | priceformat}}
                        </span>
                        <span v-else>
                            <!-- Check whether calculated live price exceeds the negotiation's max price -->
                            {{negotiation.type_id == 'FIXED' || negotiation.status_id == 'COMPLETED' ?
                                negotiation.price : negotiation.livePrice | priceformat}}
                        </span>
                    </td>
                    <td class="negotiation-status d-flex justify-content-end">
                        <span v-if="wasMyCounterAccepted[key]" class="static accepted">Counter Sold</span>
                        <span v-else>
                            <span v-if="!getNegotiationListActiveStatus[key] || negotiation.status_id !== 'ACTIVE'">
                                <span v-if="negotiation.status_id == 'COMPLETED' &&
                                    negotiation.accepting_user.account.id == userLoggedIn.id"
                                    class="static accepted">Sold</span>
                                <span v-else class="static inactive">Inactive</span>
                            </span>
                            <button v-else class="btn btn-primary accept" @click="acceptNegotiation(negotiation._id, key)">Accept</button>
                        </span>
                    </td>
                </tr>
            </tbody>
        </table>
    </div>
</template>

<script>
import { mapState, mapMutations, mapActions, mapGetters } from 'vuex'
import breakpoint from '@/mixins/index';

export default {
    mixins: [breakpoint],
    data(){
        return {
            tableHeaders: {
                SPOT: ['Buyer', 'Time Remaining', 'Terminal', 'Vol/L', 'Measurement', 'Lifting Period', 
                    'Payment Terms', 'Price', ''],
                BUNKERING: ['Buyer', 'Time Remaining', 'Vol/L', 'Measurement', 'Payment Terms', 'Price', '']
            },
            activeSortDirection: '',
            activeSortColumn: ''
        }
    },
    computed: {
        ...mapState('negotiation',['fuelGroupSelected', 'showAcceptNegotiation']),
        ...mapState('notifications', ['notificationList']),
        ...mapState('login',['userLoggedIn']),
        ...mapGetters('negotiation',['getNegotiationListTimer', 'getNegotiationListActiveStatus', 'wasMyCounterAccepted']),
        openAcceptNegotiationModal: function(){
            if (this.showAcceptNegotiation){
                let element = this.$root.$refs.acceptNegotiation.$el
                $(element).modal({backdrop: 'static', keyboard: false})
            }
        },
        negotiations: {
            get: function(){
                let stringA = ''
                let stringB = ''
                let sortedArray = []
                
                if (this.fuelGroupSelected && this.fuelGroupSelected.negotiationList){
                    switch (this.activeSortColumn){
                        case 'buyer': // Sort by buyer
                            sortedArray = this.fuelGroupSelected.negotiationList.sort((a, b) => {
                                stringA = a.buyer.lastName.toLowerCase()
                                stringB = b.buyer.lastName.toLowerCase()
                                
                                if (stringA < stringB) {return -1;}
                                if (stringA > stringB) {return 1;}
                                return 0;
                            })
    
                            if (this.activeSortDirection == 'desc'){
                                sortedArray.reverse()
                            }
    
                            return sortedArray
                        case 'terminal': // Sort by terminal
                            sortedArray = this.fuelGroupSelected.negotiationList.sort((a, b) => {
                                stringA = a.terminal.name
                                stringB = b.terminal.name
                                
                                if (stringA < stringB) {return -1;}
                                if (stringA > stringB) {return 1;}
                                return 0;
                            })
    
                            if (this.activeSortDirection == 'desc'){
                                sortedArray.reverse()
                            }
    
                            return sortedArray
                        case 'time remaining': // Sort by time remaining
                            sortedArray = this.fuelGroupSelected.negotiationList.sort((a, b) => {
                                const timeRemaining_A = new Date(a.expires) - new Date()
                                const timeRemaining_B = new Date(b.expires) - new Date()
    
                                const status_A = timeRemaining_A <= 0 && a.status_id == 'ACTIVE' ? 'EXPIRED' : a.status_id
                                const status_B = timeRemaining_B <= 0 && b.status_id == 'ACTIVE' ? 'EXPIRED' : b.status_id
    
                                /* Sort table based on status first:
                                 * 1. Active,
                                 * 2. Completed,
                                 * 3. Expired */
                                if (status_A < status_B) {
                                    return -1;
                                }
                                else if (status_A > status_B) {
                                    return 1;
                                }
                                else {
                                    /* For similar statuses, then sort based on time remaining */
                                    if (timeRemaining_A > 0 && !(timeRemaining_B > 0)){
                                        return -1;
                                    }
                                    else if (!(timeRemaining_A > 0) && timeRemaining_B > 0){
                                        return 1;
                                    }
                                    else {
                                        return timeRemaining_A - timeRemaining_B
                                    }
                                }
                            })
    
                            if (this.activeSortDirection == 'desc'){
                                sortedArray.reverse()
                            }
    
                            return sortedArray
                        case 'volume': // Sort by volume
                            sortedArray = this.fuelGroupSelected.negotiationList.sort((a, b) => a.quantity - b.quantity)
                            
                            if (this.activeSortDirection == 'desc'){
                                sortedArray.reverse()
                            }
    
                            return sortedArray
                        case 'measurement': // Sort by fuel measurement type
                            sortedArray = this.fuelGroupSelected.negotiationList.sort((a, b) => {
                                stringA = this.$root.$options.filters.fuelmeasurementtype(a.fuel_measurement_id)
                                stringB = this.$root.$options.filters.fuelmeasurementtype(b.fuel_measurement_id)
                                
                                if (stringA < stringB) {return -1;}
                                if (stringA > stringB) {return 1;}
                                return 0;
                            })
    
                            if (this.activeSortDirection == 'desc'){
                                sortedArray.reverse()
                            }
    
                            return sortedArray
                        case 'lifting period': // Sort by Lifting Period or delivery date
                            sortedArray = this.fuelGroupSelected.negotiationList.sort((a, b) => a.collectionTime - b.collectionTime)
    
                            if (this.activeSortDirection == 'desc'){
                                sortedArray.reverse()
                            }
    
                            return sortedArray
                        case 'credit days': // Sort by credit days
                            sortedArray = this.fuelGroupSelected.negotiationList.sort((a, b) => a.buyer.credit.days - b.buyer.credit.days)
    
                            if (this.activeSortDirection == 'desc'){
                                sortedArray.reverse()
                            }
    
                            return sortedArray
                        case 'price': // Sort by price
                            sortedArray = this.fuelGroupSelected.negotiationList.sort((a, b) => {
                                const price_a = a.type_id == 'FIXED' ?
                                    a.price : a.livePrice
                                const price_b = b.type_id == 'FIXED' ?
                                    b.price : b.livePrice
                                
                                return price_a - price_b
                            })
    
                            if (this.activeSortDirection == 'desc'){
                                sortedArray.reverse()
                            }
    
                            return sortedArray
                        default:
                            return this.fuelGroupSelected.negotiationList
                    }
                }
            }
        }
    },
    watch: {
        openAcceptNegotiationModal(){},
        sortTable(){}
    },
    methods: {
        ...mapMutations('negotiation', ['SELECT_TAB']),
        ...mapActions('negotiation', ['updateShowAcceptNegotiation', 'setNegotiationSelectedId',
            'showNegotiationUnavailableWindow', 'checkNegotiationAvailability']),
        ...mapActions('notifications', ['updateNotification']),
        selectTab: function(conversationIndex, negotiationId){
            /* Open negotiation tab only if the original negotiation is still active
             * or if seller's counter for this negotiation was accepted */
            if (this.getNegotiationListActiveStatus[conversationIndex] || this.wasMyCounterAccepted[conversationIndex] ||
                (this.negotiations[conversationIndex].status_id == 'COMPLETED' && 
                 this.negotiations[conversationIndex].accepting_user.account.id == this.userLoggedIn.id)){
                
                this.SELECT_TAB(conversationIndex + 1)

                if (negotiationId){
                    this.setNegotiationSelectedId(negotiationId)
                }
    
                /* Smooth scroll horizontally to the selected conversation tab */
                let selectedTab = this.$parent.$refs.conversationTab[conversationIndex]
                selectedTab.scrollIntoView({block: 'end', behavior: 'smooth'})

                /* Remove red dot notifications for this negotiation */
                this.notificationList.forEach(notification => {
                    if (notification.type_id === 'NEGOTIATION' && notification.group_id === negotiationId)
                        this.updateNotification(notification.group_id)
                })
            }
        },
        acceptNegotiation: async function(negotiationId, index){
            const negotiationAvailable = await this.checkNegotiationAvailability({negotiationId: negotiationId})

            /* Check whether negotiation is still active or not */
            if (negotiationAvailable){
                this.updateShowAcceptNegotiation(true)
            }
            else {
                this.showNegotiationUnavailableWindow(true)
            }
        },
        sortByColumn(columnHeader, direction){
            this.activeSortDirection = direction
            this.activeSortColumn = columnHeader.toLowerCase()
        }
    }
}
</script>
<style lang="scss" scoped>
@import "@/assets/scss/pages/_negotiations.scss";
</style>